import {
  selectModeIsLoadingData,
  selectModeIsTransitioning,
  selectModeName,
} from "@/store/mode-selectors";
import type { AppThunk, RootState } from "@/store/store";
import { assert } from "@faro-lotv/foundation";
import { isEqual, isPlainObject, omit } from "es-toolkit";
import { activeSheet } from "./active-sheet";
import { cameraPose } from "./camera-pose";
import { elementAndMode, isViewingMode } from "./element-and-mode";
import { mainScene } from "./main-scene";
import { splitCompare } from "./split-compare";
import { AnyTrackedState, NavigationView, TrackedState } from "./tracked-state";

/**
 * The parts of the viewer state the navigation history records and restores, in
 * restore order. To track more state, add a `TrackedState` file and list it here.
 * A part that builds on another one declares it in `restoreAfter`.
 */
const TRACKED_STATES = [
  elementAndMode,
  mainScene,
  activeSheet,
  splitCompare,
  cameraPose,
] as const;

/**
 * @param parts The tracked states in restore order
 * @throws if a part is listed before a part it is restored after
 */
export function assertRestoreOrder(parts: readonly AnyTrackedState[]): void {
  parts.forEach(({ restoreAfter = [] }, index) => {
    for (const dependency of restoreAfter) {
      const dependencyIndex = parts.indexOf(dependency);
      assert(
        dependencyIndex >= 0 && dependencyIndex < index,
        `Tracked state ${index} is listed before a part in its restoreAfter`,
      );
    }
  });
}

assertRestoreOrder(TRACKED_STATES);

/**
 * Turns a union into an intersection, e.g. `A | B` into `A & B`, so the entry
 * types of all tracked states merge into one entry. Works by placing the union in
 * a function parameter, where inferring it back yields the intersection.
 */
type UnionToIntersection<U> = (
  U extends unknown ? (union: U) => void : never
) extends (intersection: infer I) => void
  ? I
  : never;

/** The part of the entry a tracked state owns, distributed over a union of them */
type PartOf<State> = State extends TrackedState<infer Entry> ? Entry : never;

/** The viewer state a browser history entry restores, as a subset of a saved view */
export type NavigationHistoryEntry = UnionToIntersection<
  PartOf<(typeof TRACKED_STATES)[number]>
>;

/**
 * The tracked states, reading their own share of the whole entry and each
 * capturing only their part of it
 */
const trackedStates: ReadonlyArray<
  Omit<TrackedState<NavigationHistoryEntry>, "capture"> & {
    capture(state: RootState, view: NavigationView): object | undefined;
  }
> = TRACKED_STATES;

/** Keys of the parts that update the current entry in place */
const IN_PLACE_KEYS = trackedStates.flatMap(
  ({ inPlaceKeys }) => inPlaceKeys ?? [],
);

/**
 * @param a An entry
 * @param b Another entry
 * @returns whether the entries are at the same position, differing at most in
 * the parts that update an entry in place, like the camera pose
 */
export function isSamePosition(
  a: NavigationHistoryEntry | undefined,
  b: NavigationHistoryEntry | undefined,
): boolean {
  return isEqual(a && omit(a, IN_PLACE_KEYS), b && omit(b, IN_PLACE_KEYS));
}

/**
 * @param value The value to check, e.g. read from the browser history
 * @returns whether the value is a navigation history entry
 */
export function isNavigationHistoryEntry(
  value: unknown,
): value is NavigationHistoryEntry {
  return (
    isPlainObject(value) && trackedStates.every(({ isEntry }) => isEntry(value))
  );
}

/**
 * @param state The current state
 * @param view The current 3d view
 * @returns the navigation state to record, or undefined while it should not be
 * recorded (loading, transitioning or running an exclusive workflow)
 */
export function captureNavigationHistoryEntry(
  state: RootState,
  view: NavigationView,
): NavigationHistoryEntry | undefined {
  if (
    !isViewingMode(selectModeName(state)) ||
    selectModeIsTransitioning(state) ||
    selectModeIsLoadingData(state)
  ) {
    return;
  }
  const entry = {};
  for (const { capture } of trackedStates) {
    const part = capture(state, view);
    if (!part) return;
    Object.assign(entry, part);
  }
  return isNavigationHistoryEntry(entry) ? entry : undefined;
}

/**
 * Restores a recorded navigation state. Unlike `applySavedView` it leaves the
 * tool, clipping box and filters untouched. Nothing is restored outside of the
 * viewing modes, or if any part of the entry is no longer valid for the project.
 *
 * @param entry The state to restore
 * @param view The current 3d view
 * @returns a thunk restoring the state, returning whether it did
 */
export function applyNavigationHistoryEntry(
  entry: NavigationHistoryEntry,
  view: NavigationView,
): AppThunk<boolean> {
  return (dispatch, getState) => {
    const state = getState();
    const mode = selectModeName(state);
    if (
      !isViewingMode(mode) ||
      !trackedStates.every(({ canRestore }) => canRestore(entry, state))
    ) {
      return false;
    }
    for (const { restore } of trackedStates) {
      dispatch(restore(entry, view, mode));
    }
    return true;
  };
}
